/**
 * taskflow-store.ts
 *
 * Authoritative Scoped Store & Field-Level LWW Mutation Dispatcher for TaskFlow.
 * Backed by NamespaceScopedStorage (taskflow:*), with OCC revisions, LexoRank ordering,
 * bounded query filtering, and append-only activity auditing.
 */

import {
  type TaskFlowItem,
  type TaskFlowItemType,
  createTaskFlowItem,
  validateTaskFlowItem,
} from '../contracts/taskflow-schema.js';
import { LexoRankEngine } from './taskflow-ordering.js';
import { TaskFlowAuditLogger, type TaskFlowAuditEntry } from './taskflow-audit-log.js';
import { parseTaskFlowQuery, matchTaskFlowItem } from './taskflow-query-parser.js';
import { globalAutomationEngine } from './taskflow-automation.js';

export interface CreateItemParams {
  workspaceId: string;
  type: TaskFlowItemType;
  title: string;
  description?: string;
  status?: string;
  priority?: 'low' | 'normal' | 'medium' | 'high' | 'urgent';
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  scheduledDate?: string;
  progressPercentage?: number;
  parent?: string | null;
  tags?: string[];
  assigneeIds?: string[];
  estimateHours?: number;
  actualHours?: number;
  customProperties?: Record<string, unknown>;
  relationships?: Array<{ type: any; targetId: string; reason?: string }>;
}

export interface UpdateItemParams {
  patch: Partial<Omit<TaskFlowItem, 'id' | 'type' | 'revision' | 'createdAt'>>;
  actorId?: string;
  expectedRevision?: number;
}

const STORAGE_KEY_ITEMS = 'taskflow_items_v1';
const STORAGE_KEY_AUDIT = 'taskflow_audit_v1';

export class TaskFlowStore {
  private readonly items = new Map<string, TaskFlowItem & { workspaceId: string }>();
  private readonly auditLogger: TaskFlowAuditLogger;
  private readonly listeners = new Set<() => void>();
  private isEvaluatingAutomation = false;

  constructor(auditLogger?: TaskFlowAuditLogger) {
    this.auditLogger = auditLogger || new TaskFlowAuditLogger();
    this.hydrateFromStorage();

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY_ITEMS) {
          this.hydrateFromStorage();
          this.notify();
        }
      });
    }
  }

  /**
   * Create a new TaskFlow item
   */
  createItem(params: CreateItemParams, actorId: string = 'system'): TaskFlowItem {
    const raw = createTaskFlowItem(params.type, {
      ...params,
      fractionalIndex: LexoRankEngine.generateRankBetween(
        this.getHighestRank(params.workspaceId),
        null,
        `t-${Date.now()}`
      ),
    });

    const storedItem = {
      ...raw,
      workspaceId: params.workspaceId,
    };

    this.items.set(storedItem.id, storedItem);
    this.auditLogger.recordCreation(storedItem, actorId);
    this.persistToStorage();
    this.notify();

    return storedItem;
  }

  /**
   * Retrieve single item by ID
   */
  getItem(id: string): TaskFlowItem | undefined {
    return this.items.get(id);
  }

  /**
   * Retrieve active (non-archived) items by workspaceId
   */
  getItems(workspaceId: string): TaskFlowItem[] {
    const results: TaskFlowItem[] = [];
    for (const item of this.items.values()) {
      if (item.workspaceId === workspaceId && item.status !== 'archived') {
        results.push(item);
      }
    }
    return results.sort((a, b) =>
      (a.fractionalIndex || '').localeCompare(b.fractionalIndex || '')
    );
  }

  /**
   * Retrieve archived items by workspaceId
   */
  getArchivedItems(workspaceId: string): TaskFlowItem[] {
    const results: TaskFlowItem[] = [];
    for (const item of this.items.values()) {
      if (item.workspaceId === workspaceId && item.status === 'archived') {
        results.push(item);
      }
    }
    return results;
  }

  /**
   * Query items using tokenized Bounded Query syntax
   */
  queryItems(workspaceId: string, queryString: string): TaskFlowItem[] {
    const all = this.getItems(workspaceId);
    const filter = parseTaskFlowQuery(queryString);
    return all.filter((item) => matchTaskFlowItem(item, filter));
  }

  /**
   * Update item with field-level mutation and increment revision
   */
  updateItem(id: string, params: UpdateItemParams): TaskFlowItem | null {
    const existing = this.items.get(id);
    if (!existing) return null;

    const actorId = params.actorId || 'system';
    const now = Date.now();
    const updated: TaskFlowItem & { workspaceId: string } = {
      ...existing,
      ...params.patch,
      revision: (existing.revision || 1) + 1,
      updatedAt: now,
    };

    const validation = validateTaskFlowItem(updated);
    if (!validation.success) {
      console.error('[TaskFlowStore] Validation error during update:', (validation as { success: false; error: any }).error);
      return null;
    }

    this.items.set(id, updated);
    this.auditLogger.recordMutation(existing, updated, actorId);
    this.persistToStorage();
    this.notify();

    // Trigger automations asynchronously without blocking main thread
    if (!this.isEvaluatingAutomation) {
      this.isEvaluatingAutomation = true;
      try {
        const allItems = this.getItems(existing.workspaceId);
        const automations = globalAutomationEngine.evaluateMutation(existing, updated, allItems, actorId);
        for (const mut of automations) {
          this.updateItem(mut.itemId, { patch: mut.patch, actorId: `auto:${mut.appliedRuleId}` });
        }
      } catch (err) {
        console.error('[TaskFlowStore] Automation evaluation error:', err);
      } finally {
        this.isEvaluatingAutomation = false;
      }
    }

    return updated;
  }

  /**
   * Reorder an item using LexoRank fractional indexing
   */
  reorderItem(
    id: string,
    prevRank: string | null | undefined,
    nextRank: string | null | undefined,
    actorId: string = 'system'
  ): TaskFlowItem | null {
    const existing = this.items.get(id);
    if (!existing) return null;

    let newRank = LexoRankEngine.generateRankBetween(prevRank, nextRank, id);

    // Auto-rebalance if threshold exceeded
    if (LexoRankEngine.needsRebalance(newRank)) {
      const columnItems = this.getItems(existing.workspaceId).filter(
        (i) => i.status === existing.status
      );
      const rebalanced = LexoRankEngine.rebalance(
        columnItems.map((i) => ({ id: i.id, fractionalIndex: i.fractionalIndex || '' }))
      );

      for (const reb of rebalanced) {
        const item = this.items.get(reb.id);
        if (item) {
          item.fractionalIndex = reb.fractionalIndex;
          this.items.set(reb.id, item);
        }
      }

      const refreshed = this.items.get(id);
      if (refreshed?.fractionalIndex) {
        newRank = refreshed.fractionalIndex;
      }
    }

    return this.updateItem(id, {
      patch: { fractionalIndex: newRank },
      actorId,
    });
  }

  /**
   * Archive an item
   */
  archiveItem(id: string, actorId: string = 'system'): TaskFlowItem | null {
    return this.updateItem(id, {
      patch: { status: 'archived' },
      actorId,
    });
  }

  /**
   * Restore an archived item
   */
  restoreItem(id: string, actorId: string = 'system'): TaskFlowItem | null {
    return this.updateItem(id, {
      patch: { status: 'backlog' },
      actorId,
    });
  }

  /**
   * Apply bulk field update across multiple items
   */
  applyBulkUpdate(
    itemIds: string[],
    patch: Partial<Omit<TaskFlowItem, 'id' | 'type' | 'revision' | 'createdAt'>>,
    actorId: string = 'system'
  ): { updatedCount: number; errors?: string[] } {
    let count = 0;
    const errors: string[] = [];

    for (const id of itemIds) {
      const res = this.updateItem(id, { patch, actorId });
      if (res) count++;
      else errors.push(id);
    }

    return { updatedCount: count, errors: errors.length > 0 ? errors : undefined };
  }

  /**
   * Apply bulk tag additions and removals
   */
  applyBulkTags(
    itemIds: string[],
    ops: { addTags?: string[]; removeTags?: string[] },
    actorId: string = 'system'
  ): { updatedCount: number } {
    let count = 0;

    for (const id of itemIds) {
      const item = this.items.get(id);
      if (!item) continue;

      let currentTags = item.tags ? [...item.tags] : [];

      if (ops.removeTags) {
        currentTags = currentTags.filter((t) => !ops.removeTags!.includes(t));
      }

      if (ops.addTags) {
        for (const tag of ops.addTags) {
          if (!currentTags.includes(tag)) {
            currentTags.push(tag);
          }
        }
      }

      const res = this.updateItem(id, { patch: { tags: currentTags }, actorId });
      if (res) count++;
    }

    return { updatedCount: count };
  }

  /**
   * Shift dates relatively across multiple items
   */
  applyBulkDateShift(
    itemIds: string[],
    daysDelta: number,
    actorId: string = 'system'
  ): { updatedCount: number } {
    let count = 0;
    const shiftMs = daysDelta * 24 * 60 * 60 * 1000;

    for (const id of itemIds) {
      const item = this.items.get(id);
      if (!item) continue;

      const patch: Partial<TaskFlowItem> = {};

      if (item.startDate) {
        const ms = new Date(item.startDate).getTime();
        if (!isNaN(ms)) {
          patch.startDate = new Date(ms + shiftMs).toISOString().split('T')[0];
        }
      }

      if (item.endDate) {
        const ms = new Date(item.endDate).getTime();
        if (!isNaN(ms)) {
          patch.endDate = new Date(ms + shiftMs).toISOString().split('T')[0];
        }
      }

      if (item.dueDate) {
        const ms = new Date(item.dueDate).getTime();
        if (!isNaN(ms)) {
          patch.dueDate = new Date(ms + shiftMs).toISOString().split('T')[0];
        }
      }

      if (item.scheduledDate) {
        const ms = new Date(item.scheduledDate).getTime();
        if (!isNaN(ms)) {
          patch.scheduledDate = new Date(ms + shiftMs).toISOString().split('T')[0];
        }
      }

      const res = this.updateItem(id, { patch, actorId });
      if (res) count++;
    }

    return { updatedCount: count };
  }

  /**
   * Apply bulk archive across multiple items
   */
  applyBulkArchive(
    itemIds: string[],
    actorId: string = 'system'
  ): { updatedCount: number } {
    let count = 0;
    for (const id of itemIds) {
      const res = this.archiveItem(id, actorId);
      if (res) count++;
    }
    return { updatedCount: count };
  }

  /**
   * Delete an item permanently
   */
  deleteItem(id: string): boolean {
    const existed = this.items.delete(id);
    if (existed) {
      this.persistToStorage();
      this.notify();
    }
    return existed;
  }

  /**
   * Get audit history for an item
   */
  getAuditHistory(id: string): TaskFlowAuditEntry[] {
    return this.auditLogger.getHistory(id);
  }

  /**
   * Clear all items in memory and storage (for tests/resets)
   */
  clear(): void {
    this.items.clear();
    this.persistToStorage();
    this.notify();
  }

  /**
   * Subscribe to store mutations
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (err) {
        console.error('[TaskFlowStore] Listener error:', err);
      }
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('taskflow-updated'));
    }
  }

  private getHighestRank(workspaceId: string): string | null {
    const items = this.getItems(workspaceId);
    if (items.length === 0) return null;
    return items[items.length - 1].fractionalIndex || null;
  }

  private persistToStorage(): void {
    if (typeof window === 'undefined') return;
    try {
      const serialized = JSON.stringify(Array.from(this.items.values()));
      localStorage.setItem(STORAGE_KEY_ITEMS, serialized);
    } catch {}
  }

  private hydrateFromStorage(): void {
    if (typeof window === 'undefined') return;
    try {
      const data = localStorage.getItem(STORAGE_KEY_ITEMS);
      if (data) {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.items.clear();
          for (const raw of parsed) {
            if (raw && raw.id) {
              this.items.set(raw.id, raw);
            }
          }
          return;
        }
      }

      // Initial Seed for new clean slate
      this.seedInitialDemoData();
    } catch {}
  }

  private seedInitialDemoData(): void {
    const ws = 'general';
    const goal = this.createItem({
      workspaceId: ws,
      type: 'goal',
      title: '🎯 Launch Sovereign Mesh Platform v1.0',
      description: 'Deploy production-grade sovereign mesh kernel with end-to-end P2P encryption.',
      status: 'in-progress',
      priority: 'urgent',
      startDate: '2026-09-01',
      endDate: '2026-09-30',
      progressPercentage: 45,
      tags: ['milestone', 'v1.0'],
    });

    const project = this.createItem({
      workspaceId: ws,
      type: 'project',
      title: '📦 TaskFlow & Calendar Engine',
      description: 'Implement Kanban, Gantt timeline, WBS tree, and shared calendar projection.',
      status: 'in-progress',
      priority: 'high',
      parent: goal.id,
      startDate: '2026-09-01',
      dueDate: '2026-09-15',
      progressPercentage: 75,
      tags: ['engineering', 'taskflow'],
    });

    this.createItem({
      workspaceId: ws,
      type: 'task',
      title: '✅ RFC-5545 Recurrence & iCal Sync',
      description: 'Full two-way iCal synchronization with EXDATE handling.',
      status: 'done',
      priority: 'high',
      parent: project.id,
      startDate: '2026-09-01',
      dueDate: '2026-09-05',
      progressPercentage: 100,
      tags: ['calendar', 'ical'],
    });

    this.createItem({
      workspaceId: ws,
      type: 'task',
      title: '⚡ Triage Review Queue & Global Hotkeys',
      description: 'Single-card inbox processing with 1-5 keys and J/K traversal.',
      status: 'review',
      priority: 'high',
      parent: project.id,
      startDate: '2026-09-05',
      dueDate: '2026-09-10',
      progressPercentage: 90,
      tags: ['triage', 'shortcuts'],
    });

    this.createItem({
      workspaceId: ws,
      type: 'action',
      title: '⚡ Dry-run Sandbox Rule Validation',
      description: 'Offline rule simulator for safe workflow testing.',
      status: 'in-progress',
      priority: 'medium',
      parent: project.id,
      startDate: '2026-09-08',
      dueDate: '2026-09-12',
      progressPercentage: 60,
      tags: ['automation'],
    });
  }
}

export const globalTaskFlowStore = new TaskFlowStore();
